<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { readable, Readable } from "svelte/store";
  import AceEditor from "./AceEditor.svelte"
  import "brace/mode/javascript"
  import "brace/theme/solarized_dark"
  import TestCaseTabs from "./TestCaseTabs.svelte"
  import SceneNodeCanvas from "./SceneNodeCanvas.svelte"
  import Documentations from "./Documentations.svelte"
  import Concepts from "./Concepts.svelte"
  import { ExerciseData } from "../dataTypes/exerciseData"
  import { TestCase } from "../exercises/testCase"
  import type { UserExerciseData } from "../stores/userExerciseDataStore"
  import { Interpreter, State } from "../api/js-interpreter"
  import {getMethods} from '../api/reflect'
  import TypedTreeView from "./TypedTreeView.svelte"
  import { StateMachine, IState } from "../api/stateMachine"
  import { getRowAndColumn } from "../shared/exceptionUtilities"
  import * as ace from "brace"
  import { TestCasesStore } from "../stores/testCasesStore";
  import { Runner } from "../api/runner";
  import MediaControls from "./MediaControls.svelte";
  import StyleSheet from "./StyleSheet.svelte"
  import Element from "./Element.svelte"
  import ErrorDisplay from "./ErrorDisplay.svelte"
  import SyntaxErrorDisplay from "./SyntaxErrorDisplay.svelte";
  import {settingsDataStore} from "../stores/settingsStore";

  export let exerciseData:ExerciseData
  export let userExerciseData:UserExerciseData

  let isComplete:boolean = false
  let informationElement:HTMLElement|null = null
  let codeText:string = ""
  //let codeFunction:(world:any)=>void
  let testCases:Readable<Array<TestCase>> = readable<Array<TestCase>>([])
  let currentTestCaseIndex:number = 0
  let syntaxError:SyntaxError | null = null
  const dispatch = createEventDispatcher<{completed:void, save:UserExerciseData}>()
  let interpreter:Interpreter | null = null
  let selectionStartIndex:number = -1
  let selectionEndIndex:number = -1
  let selectionStartPoint:ace.Ace.Point = {row:-1, column:-1};
  let selectionEndPoint:ace.Ace.Point = {row:-1, column:-1};
  let watchValues:unknown
  let runner:Runner = new Runner();
  let aceEditor:AceEditor;
  let evaluations:Array<boolean|null> = new Array<boolean|null>();
  
  //Highlighting
  let highlightElement:HTMLElement|null = null

  onMount(()=> {
    generateTestCases()
    //console.log("noDefaultCode: " + $settingsDataStore.get("noDefaultCode"))
    if (userExerciseData.code) {
      codeText = userExerciseData.code;
    }
    else if ($settingsDataStore.get("noDefaultCode") == undefined) {
      codeText = exerciseData.code;
    }

    stateMachine = new StateMachine(new StoppedState());
    state = stateMachine.currentState; //Get reference to the store.
  })

  $: {
    if (!isComplete && $testCases.length > 0) { //if we're not already complete and have the test cases loaded
      let newIsComplete = true
      for (let index:number = 0; index < $testCases.length; index++) {
        //For code, it's only success if now successful.
        if (exerciseData.testCaseDatas[index].input == "code") {
          evaluations[index] = $testCases[index].evaluation
        }
        if (!evaluations[index]) {
          newIsComplete = false
        }
      }
      isComplete = newIsComplete
      if (isComplete && ($state instanceof StoppedState || $state instanceof TerminatedState)) {
        dispatch('completed')
      }
    }
  }

  $: {
    if (exerciseData?.information) {
      let buttons = informationElement?.getElementsByTagName("button")
      if (buttons && buttons.length > 0) {
        for (let button of buttons as HTMLCollectionOf<HTMLButtonElement>) {
          button.addEventListener("click", ()=> {
            if (highlightElement) {
              highlightElement.classList.remove("highlight")
            }
            highlightElement = document.getElementById(button.value);
            if (highlightElement) {
              highlightElement.classList.add("highlight");
            }          
          })
        }
      }
    }
  }

  $: error = $testCases[currentTestCaseIndex]?.error

  let stateMachine: StateMachine;
  let state: Readable<IState>;
  
  class StoppedState implements IState {
    enter() {
      interpreter = null
      generateTestCases()
      selectionStartIndex = -1
      selectionEndIndex = -1
    }

    exit() {
    }

    play() {
      userExerciseData.code = codeText
      dispatch('save', userExerciseData)
      stateMachine.setState(new CompilingState());
    }
  }

  class CompilingState implements IState {
    enter() {
      interpreter = createInterpreter() //if this fails, go back to Stopped!
      if (interpreter != null) {
        runner.stepMilliseconds = exerciseData.stepMilliseconds;
        runner.attach(interpreter);
        stateMachine.setState(new PlayingState());
      } else {
        stateMachine.setState(new StoppedState());
      }
    }

    exit() {
    }
  }

  class PlayingState implements IState {
    private _ignoreBreakPoint:number | undefined;
    private _currentLineNumber:number | undefined;

    constructor (ignoreBreakPoint:number | undefined = undefined) {
      this._ignoreBreakPoint = ignoreBreakPoint;
    }

    enter() {
      $testCases[currentTestCaseIndex].evaluatedEvent.add(this, this.handleEvaluated)
      $testCases[currentTestCaseIndex].actionQueue.startedEvent.add(this, this.handleActionQueueStarted)
      $testCases[currentTestCaseIndex].actionQueue.emptiedEvent.add(this, this.handleActionQueueEmptied)
      runner.steppedEventObject.add(this, this.handleStep)
      runner.exceptionedEventObject.add(this, this.handleException)
      runner.terminatedEventObject.add(this, this.handleTerminated)
      runner.unpause();
      this.run();
    }

    exit() {
      $testCases[currentTestCaseIndex].evaluatedEvent.remove(this, this.handleEvaluated)
      $testCases[currentTestCaseIndex].actionQueue.startedEvent.remove(this, this.handleActionQueueStarted)
      $testCases[currentTestCaseIndex].actionQueue.emptiedEvent.remove(this, this.handleActionQueueEmptied)
      runner.steppedEventObject.remove(this, this.handleStep)
      runner.exceptionedEventObject.remove(this, this.handleException)
      runner.terminatedEventObject.remove(this, this.handleTerminated)
    }

    step() {
      /*let stacks:Array<State> = (<Interpreter>interpreter).getStateStack();
      let stack:State = stacks[stacks.length - 1];
      watchValues = stack.scope.object.properties
      let node = stacks[stacks.length - 1].node;
      // @ts-ignore The interpretter adds start to ESTree node.
      selectionStartIndex = node.start;
      // @ts-ignore The interpretter adds end to ESTree node.
      selectionEndIndex = node.end;
      stepper.step();*/
    }

    run() {
      //this.step();
    }

    pause() {
      if (this._currentLineNumber != undefined) {
        stateMachine.setState(new PausedState(this._currentLineNumber));
      }
    }

    handleStep() {
      let stacks:Array<State> = (<Interpreter>interpreter).getStateStack();
      let stack:State = stacks[stacks.length - 1];
      watchValues = stack.scope.object.properties
      let node = stacks[stacks.length - 1].node;
      // @ts-ignore The interpretter adds start to ESTree node.
      selectionStartIndex = node.start;
      // @ts-ignore The interpretter adds end to ESTree node.
      selectionEndIndex = node.end;

      if (isLine(stacks)) {
        // @ts-ignore The interpretter adds start to ESTree node.
        this._currentLineNumber = aceEditor.getLineNumber(node.start);
        if (this._currentLineNumber != this._ignoreBreakPoint && aceEditor.hasBreakpoint(this._currentLineNumber)) {
          this.pause();
        }
      }
    }

    handleActionQueueStarted() {
      runner.pause();
    }

    handleActionQueueEmptied() {
      runner.unpause();
    }

    handleTerminated() {
      $testCases[currentTestCaseIndex].queueEvaluate();
    }

    handleException() {
      const testCase:TestCase = $testCases[currentTestCaseIndex]
      testCase.handleError(runner.error);
      
      if (runner.error && runner.error.stack) {
        const point = getRowAndColumn(runner.error.stack);
        //TODO: Select this line, column in the AceEditor.
        selectionStartPoint = point;
        selectionEndPoint = {row:point.row, column: Number.MAX_SAFE_INTEGER};
      }
      stateMachine.setState(new ExceptionState());
    }

    handleEvaluated() {
      if ($testCases[currentTestCaseIndex].evaluation)
      {
        let nextTestCaseIndex = currentTestCaseIndex
        do {
          nextTestCaseIndex = nextTestCaseIndex + 1 >= $testCases.length ? 0 : nextTestCaseIndex + 1;
          if (nextTestCaseIndex == currentTestCaseIndex) { //Success! Looped around
            stateMachine.setState(new TerminatedState());
          }
          else if (exerciseData.testCaseDatas[nextTestCaseIndex].input == "code") {
            if (evaluations[nextTestCaseIndex] != true) {
              currentTestCaseIndex = nextTestCaseIndex;
              stateMachine.setState(new CompilingState());
              break;
            }
          }
        } while (nextTestCaseIndex != currentTestCaseIndex);
      }
      else { //Do we quit on first fail?
        stateMachine.setState(new TerminatedState());
      }
    }

    stop() {
      stateMachine.setState(new StoppedState());
    }
  }

  class PausedState implements IState {
    _breakPoint:number;

    constructor (breakPoint:number) {
      this._breakPoint = breakPoint;
    }

    run():void {
      stateMachine.setState(new PlayingState(this._breakPoint));
    }

    enter():void {
      runner.pause();
    }

    exit():void {
      runner.unpause();
    }
  }

  class ExceptionState implements IState {
    enter() {
    }

    exit() {
    }

    stop() {
      stateMachine.setState(new StoppedState());
    }
  }

  class TerminatedState implements IState {
    enter():void {}
    exit():void {}
    stop() {
      stateMachine.setState(new StoppedState());
    }
  }

  function generateTestCases():void {
    if ($testCases != null) {
      for (let index = 0; index < $testCases.length; index++) {
        if (exerciseData.testCaseDatas[index].input == "interact") {
          $testCases[index].evaluatedEvent.removeAllWithThisArg(null)
        }
      }
    }
    testCases = new TestCasesStore(exerciseData.testCaseDatas).readableTestCases
    if (evaluations.length != $testCases.length) {
      evaluations.length = $testCases.length
      evaluations.fill(null)
    }
    for (let index = 0; index < exerciseData.testCaseDatas.length; index++) {
      if (exerciseData.testCaseDatas[index].input == "interact") {
        $testCases[index].evaluatedEvent.add(null, ()=> handleInteractEvaluated(index))
      }
    }
  }

  function handleStop():void {
    if ($state instanceof PlayingState || $state instanceof ExceptionState || $state instanceof TerminatedState) {
      $state.stop();
    }
  }

  function handlePause():void {
    if ($state instanceof PlayingState) {
      $state.pause();      
    }
  }

  function handlePlay():void {
    if ($state instanceof StoppedState) {
      $state.play();      
    }
    else if ($state instanceof PlayingState) {
      $state.run();
    }
    else if ($state instanceof PausedState) {
      $state.run();
    }
  }

  function handleInteractEvaluated(testCaseIndex:number):void {
      evaluations[testCaseIndex] = $testCases[testCaseIndex].evaluation;
  }

  function handleStopInteract():void {
    generateTestCases()
  }

  function handleSelected(event:CustomEvent<number>):void {
    currentTestCaseIndex = event.detail
  }

  function handleHighlight(event:CustomEvent<{startRow:number, startColumn:number, endRow:number, endColumn:number}>):void {
    selectionStartPoint = {row:event.detail.startRow, column:event.detail.startColumn };
    selectionEndPoint = {row:event.detail.endRow, column:event.detail.endColumn };
  }

  function createInterpreter():Interpreter | null {
    const testCase:TestCase = $testCases[currentTestCaseIndex]
    const rootName:string = exerciseData.rootVariableName ? exerciseData.rootVariableName : "world"
    let initRootFunction = function(interpreter:any, globalObject:any) {
      interpreter.setProperty(
        globalObject, 
        'log',
        interpreter.createNativeFunction((text:string) => { testCase.log(text);})
      );

      if (!exerciseData.rootVariableName || exerciseData.rootVariableName == "")
      {
        const realRoot = testCase.root;
        const methodNames:string[] = getMethods(realRoot);
        for(let methodName of methodNames) {
          let wrapper = (...args:any[]) => {
            return realRoot.getMethodByName(methodName).apply(realRoot, args);
          }
          interpreter.setProperty(globalObject, methodName, interpreter.createNativeFunction(wrapper));
        }
      }
      else
      {
        const proxyRoot:any = interpreter.nativeToPseudo({});
        interpreter.setProperty(
          globalObject, 
          rootName, 
          proxyRoot);
          
        const realRoot = testCase.root
        const methodNames:string[] = getMethods(realRoot);
        for(let methodName of methodNames) {
          let wrapper = (...args:any[]) => {
            return realRoot.getMethodByName(methodName).apply(realRoot, args);
          }
          interpreter.setProperty(proxyRoot, methodName, interpreter.createNativeFunction(wrapper));
        }
      }
    };
    Interpreter.PARSE_OPTIONS = {
      locations: true,
      ecmaVersion: 6
    };
    syntaxError = null;
    let interpreter = null;
    try {
      interpreter = new Interpreter(codeText, initRootFunction);
    } 
    catch (e) {
      if (e instanceof SyntaxError) {
        syntaxError = e;
      }
    }
    return interpreter;
  }

  // Is the current stack at the beginning of a new line?
  let oldStack:Array<State> = new Array<State>();
  function isLine(stack:Array<State>) {
      let state = stack[stack.length - 1];
      let node = state.node;
      let type = node.type;

      if (type !== 'VariableDeclaration' &&
          type.substr(-9) !== 'Statement') {
        // Current node is not a statement.
        return false;
      }

      if (type === 'BlockStatement') {
        // Not a 'line' by most definitions.
        return false;
      }

      if (type === 'VariableDeclaration' &&
          stack[stack.length - 2].node.type === 'ForStatement') {
        // This 'var' is not a line: for (var i = 0; ...)
        return false;
      }

      if (oldStack[oldStack.length - 1] === state) {
        // Never repeat the same statement multiple times.
        // Typically a statement is stepped into and out of.
        return false;
      }

      if (oldStack.indexOf(state) !== -1 && type !== 'ForStatement' &&
          type !== 'WhileStatement' && type !== 'DoWhileStatement') {
        // Don't revisit a statement on the stack (e.g. 'if') when exiting.
        // The exception is loops.
        return false;
      }

      oldStack = stack.slice();
      return true;
    }
</script>

<div class="flex-1 flex flex-row min-h-0">
  <div class="flex-1 overflow-y-auto scrollbar-gutter-stable">
    <div id="information" bind:this={informationElement} class="text-darkFore p-3">{@html exerciseData.information}</div>
    <div id="documentations">
      <Documentations documentationIDs={exerciseData.documentations}/>
    </div>
    <div id="concepts">
      <Concepts exerciseData={exerciseData}/>
    </div>
  </div>
  <div class="flex-1 flex flex-col min-w-0">
    <div>
      <TestCaseTabs 
        on:selected={e=>handleSelected(e)} 
        enabled={$state instanceof StoppedState || $state instanceof TerminatedState}
        evaluations={evaluations} 
        testCaseDatas={exerciseData.testCaseDatas}
        selectedIndex={currentTestCaseIndex}>
      </TestCaseTabs>
    </div>
    <div class="text-darkFore text-center">{exerciseData.goal}</div>
    <SceneNodeCanvas
      sceneNode={$testCases[currentTestCaseIndex]?.sceneNode}
      canvasWidth={$testCases[currentTestCaseIndex] ? $testCases[currentTestCaseIndex].canvasWidth : 1}
      canvasHeight={$testCases[currentTestCaseIndex] ? $testCases[currentTestCaseIndex].canvasHeight : 1}/>
    <div class="flex-1 overflow-y-auto">
      <div class="text-darkFore bg-darkBack whitespace-pre-wrap">{$testCases[currentTestCaseIndex]?.logText}</div>
      {#if syntaxError}
      <SyntaxErrorDisplay syntaxError={syntaxError} on:highlight={handleHighlight} />
      {/if} 
      {#if error}
      <ErrorDisplay
        error={error}
        on:highlight={handleHighlight}
      />
      {/if}
    </div>
  </div>
  <div class="flex-1 flex flex-col">
    {#if exerciseData.testCaseDatas[currentTestCaseIndex].input == "code"}
    <div class="flex-1" id="code_editor">
      <AceEditor
        class="w-full h-full"
        bind:this={aceEditor}
        theme="solarized_dark"
        lang="javascript"
        options={{fontSize:"1rem", showLineNumbers:false, highlightActiveLine:false}}
        bind:value={codeText}
        selectionStartIndex={selectionStartIndex}
        selectionEndIndex={selectionEndIndex}
        selectionStartPoint={selectionStartPoint}
        selectionEndPoint={selectionEndPoint}
        allowBreakpoints={exerciseData.allowBreakpoints}
      />
    </div>
    {:else if exerciseData.testCaseDatas[currentTestCaseIndex].input == "interact"}
    <StyleSheet styles={$testCases?.[currentTestCaseIndex]?.styleSheet}>
      <Element
        height="100%"
        element={$testCases?.[currentTestCaseIndex]?.getElement(document)}
      />
    </StyleSheet>
    {/if}
    {#if exerciseData.allowVariables}
    <TypedTreeView
      class="flex-1 tree-view"
      data={watchValues}
    />
    {/if}
    {#if exerciseData.testCaseDatas[currentTestCaseIndex].input == "code"}
    <MediaControls
      on:stop = {handleStop}
      on:pause = {handlePause}
      on:play = {handlePlay}
      showRewind={false}
      enableStop={$state instanceof PlayingState || $state instanceof ExceptionState || $state instanceof TerminatedState}
      enablePause={$state instanceof PlayingState}
      enablePlay={$state instanceof StoppedState || $state instanceof PausedState}
    />
    {:else if exerciseData.testCaseDatas[currentTestCaseIndex].input == "interact"}
    <MediaControls
      on:stop = {handleStopInteract}
      showPlay={true}
      showRewind={false}
      showPause={false}
      enableStop={$testCases?.[currentTestCaseIndex]?.isDirty}
      enablePause={false}
      enablePlay={false}
    />
    {/if}
  </div>
</div>

<style>
  div :global(code) {
    @apply my-3;
    @apply p-3;
    @apply block;
		@apply whitespace-pre-wrap;
    background-color: var(--color-base02);
    color: var(--color-base1);
  }

  #information :global(ol) {
    @apply pl-4;
    @apply list-outside;
    @apply list-decimal;
    @apply mt-2;
    @apply mb-2;
  }

  #information :global(ul) {
    @apply pl-4;
    @apply list-outside;
    @apply list-disc;
    @apply mt-2;
    @apply mb-2;
  }

  div :global(li:not(:last-child)) {
    @apply mb-1;
  }

  :global(.ace_gutter-cell.ace_breakpoint) { 
    background: var(--color-red);
    clip-path: polygon(0% 5%, 75% 5%, 100% 50%, 75% 95%, 0% 95%);
  }

  :global(.highlight) {
    @apply border-2;
    @apply border-blue;
  }

  :global(button.link_button) {
    @apply text-blue;
    @apply underline;
  }

  .scrollbar-gutter-stable {
    scrollbar-gutter: stable;
  }
</style>
